import type { OrderStatus, InvoiceStatus } from '../types'
import { STATUS_LABELS, INVOICE_STATUS_LABELS } from '../types'

const ORDER_MAIN_FLOW: OrderStatus[] = [
  'received', 'design', 'production', 'ready_to_ship', 'shipped', 'completed',
]

type NodeVariant = 'past' | 'current' | 'current-complete' | 'current-cancelled' | 'inactive'

function StateNode({ label, variant }: { label: string; variant: NodeVariant }) {
  const base: React.CSSProperties = {
    borderRadius: 4,
    padding: '3px 9px',
    fontSize: 11,
    whiteSpace: 'nowrap',
    userSelect: 'none',
  }
  switch (variant) {
    case 'past':
      return <span style={{ ...base, background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7', fontWeight: 500 }}>{label}</span>
    case 'current':
      return <span style={{ ...base, background: 'var(--color-primary)', color: 'white', border: '2px solid var(--color-primary)', fontWeight: 600 }}>{label}</span>
    case 'current-complete':
      return <span style={{ ...base, background: '#059669', color: 'white', border: '2px solid #059669', fontWeight: 600 }}>{label}</span>
    case 'current-cancelled':
      return <span style={{ ...base, background: 'var(--color-danger)', color: 'white', border: '2px solid var(--color-danger)', fontWeight: 600 }}>{label}</span>
    default:
      return <span style={{ ...base, background: 'transparent', color: 'var(--color-muted)', border: '1px solid var(--color-border)' }}>{label}</span>
  }
}

function Arrow({ past }: { past: boolean }) {
  return (
    <span style={{ color: past ? '#34d399' : '#e5e7eb', fontSize: 12, flexShrink: 0, userSelect: 'none' }}>→</span>
  )
}

export function OrderStateMachine({ currentStatus }: { currentStatus: OrderStatus | null }) {
  const curIdx = currentStatus !== null ? ORDER_MAIN_FLOW.indexOf(currentStatus) : -1

  const getVariant = (status: OrderStatus, idx: number): NodeVariant => {
    if (currentStatus === null) return 'inactive'
    if (status === 'completed') return currentStatus === 'completed' ? 'current-complete' : 'inactive'
    if (status === currentStatus) return 'current'
    if (idx < curIdx) return 'past'
    return 'inactive'
  }

  const cancelledVariant: NodeVariant = currentStatus === 'cancelled' ? 'current-cancelled' : 'inactive'

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
      {ORDER_MAIN_FLOW.map((status, i) => (
        <span key={status} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <StateNode label={STATUS_LABELS[status]} variant={getVariant(status, i)} />
          {i < ORDER_MAIN_FLOW.length - 1 && <Arrow past={curIdx > i} />}
        </span>
      ))}
      <span style={{ marginLeft: 8 }}>
        <StateNode label={STATUS_LABELS['cancelled']} variant={cancelledVariant} />
      </span>
    </div>
  )
}

export function InvoiceStateMachine({ currentStatus }: { currentStatus: InvoiceStatus | null }) {
  const shippedVariant: NodeVariant =
    currentStatus === null ? 'inactive' :
    currentStatus === 'shipped' ? 'current' :
    'past'

  const paidVariant: NodeVariant = currentStatus === 'paid' ? 'current-complete' : 'inactive'
  const cancelledVariant: NodeVariant = currentStatus === 'cancelled' ? 'current-cancelled' : 'inactive'

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 16 }}>
      <StateNode label={INVOICE_STATUS_LABELS['shipped']} variant={shippedVariant} />
      <Arrow past={currentStatus !== null && currentStatus !== 'shipped'} />
      <StateNode label={INVOICE_STATUS_LABELS['paid']} variant={paidVariant} />
      <span style={{ marginLeft: 8 }}>
        <StateNode label={INVOICE_STATUS_LABELS['cancelled']} variant={cancelledVariant} />
      </span>
    </div>
  )
}
